<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'

const props = defineProps({
  gamedate: {
    type: String,
    default: null
  }
})

const timeRemaining = ref(null)
const showGameClosedOverlay = ref(false)
let countdownInterval = null
let overlayTimeout = null

const parseGameDate = (gamedate) => {
  if (!gamedate) return null
  
  // Handle European format: dd.mm.yyyy HH:mm (e.g., "05.11.2025 15:15")
  const europeanFormat = /^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})$/
  const match = String(gamedate).match(europeanFormat)
  
  if (match) {
    const [, day, month, year, hour, minute] = match
    // Create date in format: YYYY-MM-DDTHH:mm (ISO format)
    const isoDate = `${year}-${month}-${day}T${hour}:${minute}:00`
    const d = new Date(isoDate)
    return isNaN(d.getTime()) ? null : d
  }
  
  // Try parsing the date - handle other formats
  const tryParse = (dateStr) => {
    const d = new Date(dateStr)
    return isNaN(d.getTime()) ? null : d
  }
  return tryParse(gamedate) || tryParse(String(gamedate).replace(' ', 'T'))
}

const updateCountdown = () => {
  if (!props.gamedate) {
    timeRemaining.value = null
    return
  }

  const gameDate = parseGameDate(props.gamedate)
  if (!gameDate) {
    timeRemaining.value = null
    return
  }

  const now = new Date()
  const diff = gameDate.getTime() - now.getTime()
  
  if (diff <= 0) {
    // Game has started - show overlay if we were counting down (within 5 minutes)
    const wasCountingDown = timeRemaining.value && timeRemaining.value.totalSeconds < 300
    timeRemaining.value = null
    
    if (wasCountingDown && !showGameClosedOverlay.value) {
      showGameClosedOverlay.value = true
      // Auto-close overlay after 5 seconds
      if (overlayTimeout) {
        clearTimeout(overlayTimeout)
      }
      overlayTimeout = setTimeout(() => {
        showGameClosedOverlay.value = false
      }, 5000)
    }
    return
  }

  const totalSeconds = Math.floor(diff / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  timeRemaining.value = {
    totalSeconds,
    minutes,
    seconds,
    formatted: `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }
}

const showCountdown = computed(() => {
  return timeRemaining.value && timeRemaining.value.totalSeconds < 300 // Less than 5 minutes
})

const isLessThanOneMinute = computed(() => {
  return timeRemaining.value && timeRemaining.value.totalSeconds < 60
})

// Watch for changes in gamedate and start/stop countdown
watch(() => props.gamedate, () => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
  updateCountdown()
  if (props.gamedate) {
    countdownInterval = setInterval(updateCountdown, 1000)
  }
}, { immediate: true })

const closeOverlay = () => {
  showGameClosedOverlay.value = false
  if (overlayTimeout) {
    clearTimeout(overlayTimeout)
    overlayTimeout = null
  }
}

onUnmounted(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
    countdownInterval = null
  }
  if (overlayTimeout) {
    clearTimeout(overlayTimeout)
    overlayTimeout = null
  }
})
</script>

<template>
  <div>
    <div v-if="showCountdown" 
         :class="[
           'px-3 py-1 rounded-lg text-white font-semibold text-sm flex items-center gap-2 ml-2 bg-red-500 transition-transform duration-300',
           isLessThanOneMinute ? 'scale-200' : ''
         ]">
      <i class="fas fa-clock"></i>
      <span>{{ timeRemaining?.formatted }}</span>
    </div>

    <!-- Game Closed Overlay -->
    <Transition name="fade">
      <div v-if="showGameClosedOverlay" 
           class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
           @click="closeOverlay">
        <div class="bg-white rounded-lg shadow-xl p-8 max-w-md mx-4 text-center">
          <div class="text-6xl mb-4">
            <i class="fas fa-check-circle text-green-500"></i>
          </div>
          <h2 class="text-3xl font-bold text-gray-800 mb-2"> Fixture.Closed</h2>
          <p class="text-gray-600">The game has started and pre match is now closed.</p>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.scale-200 {
  transform: scale(2) translateX(15px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

